import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import type { ILineItemTaxApi } from './api';
import { LineItemTaxApi } from './api';
import type {
  CountriesTaxCodesControllerResultTypes,
  CountryResultDto,
  CreateLineItemConfigurationDto,
  LineItemConfigurationControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import type {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

const ApiContext = createContext<ILineItemTaxApi | undefined>(undefined);
const { useApi: useLineItemTaxApi, ApiProvider: LineItemTaxApiProvider } =
  makeApiProvider({
    name: 'LineItemTax',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new LineItemTaxApi(tenantId, auth, apiUrl),
  });

export {
  useLineItemTaxApi,
  LineItemTaxApiProvider,
  ApiContext as LineItemTaxApiContext,
};
export type { ILineItemTaxApi } from './api';

export const GET_TAX_LINE_CONFIGURATIONS = (
  globalServiceProviderId: string | undefined
): QueryKey => ['get-tax-line-configurations', globalServiceProviderId];

export const GET_TAX_CODES_BY_COUNTRY = (
  countryId: string | undefined
): QueryKey => ['get-tax-codes-by-country', ...(countryId ? [countryId] : [])];

export const GET_ALL_COUNTRIES = (): QueryKey => ['get-all-countries'];

export const CREATE_TAX_LINE_CONFIGURATION = (
  globalServiceProviderId: string | undefined
): QueryKey => ['create-tax-line-configurations', globalServiceProviderId];

export const DELETE_TAX_LINE_CONFIGURATION = (
  globalServiceProviderId: string | undefined
): QueryKey => ['create-tax-line-configurations', globalServiceProviderId];

export const useGetTaxCodesByCountry = (
  countryId: string | undefined
): UseQueryResult<CountriesTaxCodesControllerResultTypes['getTaxCodes']> => {
  return useApiQuery(
    useLineItemTaxApi,
    GET_TAX_CODES_BY_COUNTRY(countryId),
    (api) => {
      if (!countryId) {
        throw new Error('countryId is required');
      }

      return api.getTaxCodesByCountry({
        params: {
          countryId,
        },
      });
    },
    { enabled: !!countryId }
  );
};

export interface CountryWithTranslatedLabel extends CountryResultDto {
  label: string;
  value: string;
}

export const useGetAllCountries = (
  options?: UseQueryOptions<
    CountriesTaxCodesControllerResultTypes['getCountries']
  >
): UseQueryResult<CountryWithTranslatedLabel[]> => {
  const { t } = useTranslation();

  const translateAndSortCountries = useCallback(
    (
      countries: CountryResultDto[]
    ): CountryWithTranslatedLabel[] | undefined => {
      const options = countries?.map((country) => {
        return {
          ...country,
          value: country.id,
          label: t(`country.${country.isoCode}`),
        };
      });

      options?.sort((a, b) => a.label.localeCompare(b.label));
      return options;
    },
    [t]
  );

  return useApiQuery(
    useLineItemTaxApi,
    GET_ALL_COUNTRIES(),
    (api) => {
      return api.getAllCountries();
    },
    {
      ...options,
      select: (data) => translateAndSortCountries(data) ?? [],
    }
  );
};

export const useGetTaxLineConfigurations = (
  globalServiceProviderId: string | undefined
): UseQueryResult<
  LineItemConfigurationControllerResultTypes['getLineItemConfigurations']
> => {
  return useApiQuery(
    useLineItemTaxApi,
    GET_TAX_LINE_CONFIGURATIONS(globalServiceProviderId),
    (api) => {
      if (!globalServiceProviderId) {
        throw new Error('globalServiceProviderId is required');
      }
      return api.getLineItemConfigurations({
        params: {
          globalServiceProviderId,
        },
      });
    },
    {
      enabled: !!globalServiceProviderId,
    }
  );
};

export const useCreateLineItemConfiguration = (
  globalServiceProviderId: string | undefined
): UseMutationResult<
  LineItemConfigurationControllerResultTypes['postLineItemConfiguration'],
  unknown,
  CreateLineItemConfigurationDto
> => {
  const api = useLineItemTaxApi();
  const queryClient = useQueryClient();
  return useMutation<
    LineItemConfigurationControllerResultTypes['postLineItemConfiguration'],
    unknown,
    CreateLineItemConfigurationDto
  >(
    CREATE_TAX_LINE_CONFIGURATION(globalServiceProviderId),
    (data) => {
      if (!globalServiceProviderId) {
        throw new Error('globalServiceProviderId is required');
      }
      return api.createLineItemConfiguration({
        params: {
          globalServiceProviderId,
        },
        body: data,
      });
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(
          GET_TAX_LINE_CONFIGURATIONS(globalServiceProviderId)
        );
      },
    }
  );
};

export const useDeleteLineItemConfiguration = (
  globalServiceProviderId: string | undefined
): UseMutationResult<
  LineItemConfigurationControllerResultTypes['deleteLineItemConfiguration'],
  unknown,
  {
    id: string;
  }
> => {
  const api = useLineItemTaxApi();
  const queryClient = useQueryClient();
  return useMutation<
    LineItemConfigurationControllerResultTypes['deleteLineItemConfiguration'],
    unknown,
    {
      id: string;
    }
  >(
    DELETE_TAX_LINE_CONFIGURATION(globalServiceProviderId),
    (data) => {
      if (!globalServiceProviderId) {
        throw new Error('globalServiceProviderId is required');
      }
      return api.deleteLineItemConfiguration({
        params: {
          globalServiceProviderId,
          id: data.id,
        },
      });
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(
          GET_TAX_LINE_CONFIGURATIONS(globalServiceProviderId)
        );
      },
    }
  );
};
